
export interface ProcessedDestinations {
    destinations: string[];
    weights: Record<string, number>;
    limits: Record<string, number>;
    mappings: Record<string, string>;
}

/**
 * Processes a list of raw destinations (string or object) into a structured format
 * for backend routing configuration.
 * 
 * Handles trimming of values and extraction of weights, limits, and mappings.
 * 
 * @param rawDestinations - Array of strings or objects { value, weight?, limit?, mapping? }
 * @returns ProcessedDestinations object
 */
export function processDestinations(rawDestinations: any[]): ProcessedDestinations {
    const destinations: string[] = [];
    const weights: Record<string, number> = {};
    const limits: Record<string, number> = {};
    const mappings: Record<string, string> = {};

    if (!Array.isArray(rawDestinations)) {
        return { destinations, weights, limits, mappings };
    }

    rawDestinations.forEach((d: any) => {
        let val: string;

        if (typeof d === 'string') {
            val = d.trim();
        } else {
            val = (d.value || '').trim();
        }

        if (val === '') {
            return;
        }

        destinations.push(val);

        if (typeof d !== 'string') {
            // Processing weights
            if (d.weight !== undefined) {
                const weight = parseInt(d.weight);
                if (!isNaN(weight)) {
                    // Only add weight if it makes sense (e.g. > 0), 
                    // or if we want to explicitly preserve 0 for some reason?
                    // Existing logic was: if (d.weight) ... or if (d.weight && d.weight !== 1)
                    // Let's be consistent:
                    // domain-management: if (d.weight) ... 
                    // rule-management: if (d.weight && d.weight !== 1) ...
                    // To be safe and generic, we should probably store it if it exists.
                    // But typically default weight is 1. Storing 1 is redundant but harmless.
                    weights[val] = weight;
                }
            }

            // Processing limits
            if (d.limit !== undefined) {
                const limit = parseInt(d.limit);
                if (!isNaN(limit)) {
                    limits[val] = limit;
                }
            }

            // Processing mappings
            if (d.mapping !== undefined) {
                mappings[val] = d.mapping;
            }
        }
    });

    return { destinations, weights, limits, mappings };
}
